import React, { useState, useEffect } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import "./clientPriceChanges.css";

const money = (n) =>
    `AED ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? DateTime.fromISO(d, { zone: "Asia/Dubai" }).toFormat("dd MMM yy, HH:mm") : "—");

// The action taken on the money for this edit → { text, tone } or null.
const settlementLine = (h) => {
    const amt = money(h.settlement_amount);
    switch (h.settlement) {
        case "refunded_to_wallet": return { text: `${amt} refunded to your wallet`, tone: "refund" };
        case "charged_to_wallet": return { text: `${amt} charged from your wallet`, tone: "charge" };
        case "charged_to_card": return { text: `${amt} charged to your card`, tone: "charge" };
        case "added_to_invoice": return { text: `${amt} added as pending · on your next invoice`, tone: "pending" };
        case "reduced": return { text: `${amt} less on your next invoice`, tone: "refund" };
        default: return null;
    }
};

/**
 * PRICE CHANGES + PENDING for a client's booking (owner 2026-07-21 — replaces
 * price adjustments). Shows WHAT changed on each edit and the total before→after,
 * plus how much is still pending on this booking (billed on the client's next
 * invoice). No separate charge to approve — the difference is simply invoiced.
 *
 * Rendered as a `.cbd__card` so it matches every other section on the booking
 * details page, and placed directly under the Transactions card (2026-07-21).
 */
export default function ClientPriceChanges({ bookingId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const res = await axios.get(`/api/client-requests/bookings/${bookingId}/price-changes`);
                if (alive) setData(res.data);
            } catch (e) {
                if (alive) setData(null);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [bookingId]);

    if (loading || !data) return null;
    const history = Array.isArray(data.price_changes) ? data.price_changes : [];
    const pending = Number(data.pending || 0);
    // Price CHANGES only (2026-08-04): a booking's own unpaid total is
    // not a price change — it already shows on the charges card. With no
    // actual change history, this card has nothing to say.
    if (history.length === 0) return null;

    return (
        <div className="cbd__card cpc">
            <div className="cpc__head">
                <h3 className="cbd__cardTitle">Price Changes</h3>
                {/* Same fix as the staff card (2026-08-30): the paid badge no
                    longer needs a non-zero pending. It only ever required one
                    because pending stayed stuck at the booking total on an
                    invoice-billed booking; now that a paid invoice clears it,
                    gating on pending would hide the badge on exactly the
                    bookings that are paid. A client must never be shown a
                    pending figure for money they have already sent. */}
                {data.invoice_paid ? (
                    <span className="cpc__pending cpc__pending--paid">
                        Paid{data.invoice_number ? ` · invoice ${data.invoice_number}` : ''}
                    </span>
                ) : pending > 0.009 ? (
                    <span className="cpc__pending">
                        Pending {money(pending)} · {data.invoiced
                            ? `invoiced${data.invoice_number ? ` ${data.invoice_number}` : ''}`
                            : 'on your next invoice'}
                    </span>
                ) : null}
            </div>

            {history.length === 0 ? (
                <div className="cpc__empty">
                    {pending > 0.009
                        ? `This booking has ${money(pending)} pending — it will appear on your next invoice.`
                        : "No price changes on this booking."}
                </div>
            ) : (
                <ul className="cpc__list">
                    {history.map((h) => (
                        <li key={h.id} className="cpc__item">
                            <div className="cpc__itemTop">
                                <span className="cpc__lines">
                                    {(h.changes || []).length ? h.changes.join(" · ") : "Price updated"}
                                </span>
                                {h.old_total != null && h.new_total != null && (
                                    <span className={`cpc__delta ${Number(h.new_total) >= Number(h.old_total) ? "cpc__delta--up" : "cpc__delta--down"}`}>
                                        {money(h.old_total)} → {money(h.new_total)}
                                    </span>
                                )}
                            </div>
                            {settlementLine(h) && (
                                <div className={`cpc__action cpc__action--${settlementLine(h).tone}`}>
                                    {settlementLine(h).text}
                                </div>
                            )}
                            <div className="cpc__meta">
                                {fmtDate(h.created_at)}
                                {h.changed_by_name ? ` · by ${h.changed_by_name}` : ""}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
